import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import FieldFormFields from 'components/forms/FieldFormFields';

import useField from 'hooks/useField';
import useFieldBoundaryAssignments from 'hooks/useFieldBoundaryAssignments';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { CreateFieldFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateFieldForm: FunctionComponent<Props> = ({
    className = '',
    defaultValues,
    onCancelClose,
    onClose,
    onCreated,
    requestingClose,
    testId = TEST_ID
}) => {
    const { createField, isLoading } = useField();
    const { saveBoundaryAssignments } = useFieldBoundaryAssignments();
    const { CREATE } = useTranslation();
    const navigate = useNavigate();
    const { fieldDetailsPage } = usePageRoutes();

    const form = useForm<CreateFieldFormSchema>({
        defaultValues,
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const createdFieldId = useRef<string | undefined>(undefined);

    const onSubmit = async (data: CreateFieldFormSchema) => {
        const { boundaries, farm, name, primaryBoundary } = data;

        // An earlier submit may have created the field and then failed while assigning its
        // boundaries. Reusing that id means a retry finishes the boundaries instead of creating
        // a second field.
        const fieldId =
            createdFieldId.current ??
            (await createField({ farmId: farm.value, field: { name } }))?.id;

        if (!fieldId) {
            return;
        }

        createdFieldId.current = fieldId;

        // The field only exists now, so boundary selections — including any move confirmed
        // while filling the form — are applied here rather than at selection time.
        const isSaved = await saveBoundaryAssignments({
            boundaries: boundaries ?? [],
            initialBoundaries: [],
            initialPrimary: null,
            primary: primaryBoundary ?? null,
            siteId: fieldId
        });

        // withRetry has already surfaced the failure, so stay on the form rather than reporting
        // a create that only partly landed. Assignment is idempotent, so submitting again
        // retries just the boundaries.
        if (!isSaved) {
            return;
        }

        onCreated?.();
        navigate(fieldDetailsPage(fieldId));
    };

    return (
        <VerticalContainer className={`${styles.createFieldForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <FieldFormFields />

                    <FormSubmitPanel
                        buttonText={CREATE}
                        disabled={isLoading}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateFieldForm;
